<script setup lang="ts">
  import { UiButton } from '@logistiek-zonder-papier/lzp-library'
  import { useTranslation } from 'i18next-vue'
  import { computed } from 'vue'

  const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

  const props = defineProps<{
    totalPages: number
  }>()

  const page = defineModel<number>({ required: true })
  const pageSize = defineModel<number>('pageSize', { required: true })

  const { t: translate } = useTranslation()

  const pages = computed(() => Array.from({ length: props.totalPages }, (_, index) => index + 1))

  const pageSizeOptions = computed(() =>
    [...new Set([...DEFAULT_PAGE_SIZE_OPTIONS, pageSize.value])].sort(
      (first, second) => first - second
    )
  )

  function goPrevious() {
    page.value = Math.max(1, page.value - 1)
  }

  function goNext() {
    page.value = Math.min(props.totalPages, page.value + 1)
  }
</script>

<template>
  <div class="pagination">
    <div class="page-navigation">
      <UiButton
        class="page-button"
        size="small"
        icon="chevronLeft"
        :disabled="page <= 1"
        @click="goPrevious"
      />

      <select v-model.number="page" class="select" :aria-label="translate('table.selectPage')">
        <option v-for="pageNumber in pages" :key="pageNumber" :value="pageNumber">
          {{ translate('table.pageOf', { page: pageNumber, total: totalPages }) }}
        </option>
      </select>

      <UiButton
        class="page-button"
        size="small"
        icon="chevronRight"
        :disabled="page >= totalPages"
        @click="goNext"
      />
    </div>

    <label class="page-size">
      {{ translate('table.pageSize') }}
      <select v-model.number="pageSize" class="select">
        <option v-for="option in pageSizeOptions" :key="option" :value="option">
          {{ option }}
        </option>
      </select>
    </label>
  </div>
</template>

<style scoped>
  .pagination {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: var(--spacing-s);
    padding: var(--spacing-s);
  }

  .page-navigation {
    grid-column: 2;
    display: flex;
    align-items: center;
    gap: var(--spacing-s);
  }

  .page-button {
    border-radius: 50%;
  }

  .page-size {
    grid-column: 3;
    justify-self: end;
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: 0.875rem;
    white-space: nowrap;
  }

  .select {
    height: var(--components-component-height-small);
    min-width: 4rem;
    padding: 0 var(--spacing-xs);
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--spacing-xs);
    background: var(--colours-grey-1);
    box-shadow: 0 1px 2px 0 var(--colours-shadow-2);
    font-size: 0.875rem;
  }

  .page-navigation .select {
    min-width: 6rem;
  }
</style>
